import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { T, useLang } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";
import { PressStrip } from "@/components/PressStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TARGET_IMG =
  "https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjb25zdWx0YXRpb24lMjBkZXNrJTIwYnVzaW5lc3MlMjBkb2N1bWVudHN8ZW58MHx8fHwxNzkyOTY3NTI5fDA&ixlib=rb-4.1.0&q=85";

const OdometerDigit = ({ d }) => (
  <span className="tm-odo">
    <span className="tm-reel" style={{ "--d": d }}>
      0<br />1<br />2<br />3<br />4<br />5<br />6<br />7<br />8<br />9
    </span>
  </span>
);

export default function Landing() {
  const [loading, setLoading] = useState(null);
  const [lang, setLang] = useLang();
  const t = T[lang].landing;
  const PACKAGES = t.packages;

  const handleCheckout = async (pkg) => {
    setLoading(pkg.key);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: pkg.key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Pagamento non disponibile. Riprova tra poco.");
      setLoading(null);
    }
  };

  return (
    <div className="bg-[#200910] text-[#faf6ef] min-h-screen font-sans selection:bg-[#d3b870] selection:text-[#200910]">
      {/* NAV */}
      <nav className="border-b border-[#4d1a2a] bg-[#200910]/90 sticky top-0 z-40 backdrop-blur-md px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center shrink-0" data-testid="brand-logo-link">
            <img src="/logo-trust-master.png" alt="Trust Master" className="h-9 w-auto shrink-0" style={{ aspectRatio: "485/155" }} data-testid="brand-logo" />
          </a>
          <div className="flex items-center gap-6 text-xs uppercase font-mono-lbl tracking-[0.14em]">
            <a href="#diagnosi" data-testid="nav-diagnosi-link" className="hidden md:inline text-[#b4939c] hover:text-[#d3b870] transition-colors">{t.nav_diagnosi}</a>
            <a href="#percorso" data-testid="nav-percorso-link" className="hidden md:inline text-[#b4939c] hover:text-[#d3b870] transition-colors">{t.nav_percorso}</a>
            <a href="#parlano-di-noi" data-testid="nav-press-link" className="hidden md:inline text-[#b4939c] hover:text-[#d3b870] transition-colors">{t.nav_press}</a>
            <div className="hidden md:block"><LangSwitcher lang={lang} setLang={setLang} /></div>
            <a href="#pacchetti" data-testid="nav-booking-cta" className="px-3 py-2 md:px-4 border border-[#d3b870] text-[#d3b870] hover:bg-[#d3b870] hover:text-[#200910] transition-colors font-semibold text-[10px] md:text-xs">{t.nav_cta}</a>
          </div>
        </div>
        <div className="md:hidden border-t border-[#4d1a2a] mt-3 pt-2 flex justify-end" data-testid="mobile-lang-bar">
          <LangSwitcher lang={lang} setLang={setLang} />
        </div>
      </nav>

      {/* HERO */}
      <section className="bg-[#200910] text-[#faf6ef] py-24 md:py-32 px-6 border-b border-[#4d1a2a]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-8">
            <span className="w-2 h-2 bg-[#d3b870] inline-block"></span>
            <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]" data-testid="hero-kicker">{t.hero_kicker}</span>
          </div>

          <h1 className="font-display uppercase tracking-[-0.03em] leading-[0.95] text-[clamp(40px,5.5vw,88px)] text-[#faf6ef] mb-8" data-testid="hero-title">
            <span className="tm-ln"><span>{t.hero_title_1}</span></span>
            <span className="tm-ln"><span className="text-[#d3b870]">{t.hero_title_2}</span></span>
          </h1>

          <p className="tm-sub-reveal text-lg md:text-xl text-[#b4939c] max-w-2xl leading-relaxed mb-10" data-testid="hero-subtitle">
            {t.hero_sub}
          </p>

          <div className="tm-cta-reveal flex flex-wrap items-center gap-4">
            <a href="#pacchetti" data-testid="hero-cta-button" className="px-8 py-4 bg-[#d3b870] text-[#200910] font-display uppercase tracking-wider text-base hover:bg-[#ecd9ab] transition-colors shadow-lg">
              {t.hero_cta}
            </a>
            <span className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c]">{t.hero_note}</span>
          </div>

          {/* Trust Metrics Strip */}
          <div className="mt-20 pt-10 border-t border-[#4d1a2a] grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="trust-metrics">
            <div>
              <div className="text-3xl md:text-4xl font-display text-[#d3b870] flex items-center">
                <OdometerDigit d={2} />
                <OdometerDigit d={4} />
                <span className="text-xl ml-1 font-sans">{t.m1_s}</span>
              </div>
              <p className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c] mt-1">{t.m1_l}</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-display text-[#d3b870] flex items-center">
                <span className="text-2xl mr-1">+</span>
                <OdometerDigit d={9} />
                <OdometerDigit d={0} />
                <OdometerDigit d={0} />
              </div>
              <p className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c] mt-1">{t.m2_l}</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-display text-[#faf6ef]">14 <span className="text-sm font-sans text-[#d3b870]">{t.m3_s}</span></div>
              <p className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c] mt-1">{t.m3_l}</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-display text-[#faf6ef]">100%</div>
              <p className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c] mt-1">{t.m4_l}</p>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#33101c] border-b border-[#4d1a2a] py-4 overflow-hidden">
        <div className="tm-mq-track font-display text-sm uppercase tracking-[0.24em] text-[#d3b870]/80 gap-12 flex items-center">
          {[...t.marquee, ...t.marquee].map((m, i) => (
            <span key={i}>• {m}</span>
          ))}
        </div>
      </div>

      {/* TARGET */}
      <section id="diagnosi" className="bg-[#200910] text-[#faf6ef] py-24 px-6 border-b border-[#4d1a2a]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.dg_kicker}</span>
              <h2 className="font-display uppercase text-[clamp(32px,4vw,56px)] leading-[0.98] mt-2 text-[#faf6ef]">
                {t.dg_title}
              </h2>
              <p className="text-sm text-[#b4939c] mt-4 leading-relaxed">
                {t.dg_lead}
              </p>
              <figure className="mt-8 relative aspect-[4/3] bg-[#33101c] border border-[#4d1a2a] overflow-hidden" data-testid="target-image">
                <img src={TARGET_IMG} alt={t.dg_figcaption} className="w-full h-full object-cover" loading="lazy" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5 bg-[#200910]/80 text-xs uppercase font-mono-lbl tracking-[0.14em] text-[#d3b870]">{t.dg_figcaption}</figcaption>
              </figure>
            </div>

            <div className="md:col-span-7 space-y-4">
              <div className="p-6 bg-[#33101c] border border-[#4d1a2a] hover:border-[#d3b870] transition-colors">
                <span className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c]">{t.dg1_k}</span>
                <h3 className="font-display uppercase text-lg text-[#faf6ef] mt-2">{t.dg1_t}</h3>
                <p className="text-sm text-[#b4939c] mt-2">{t.dg1_d}</p>
              </div>
              <div className="p-6 bg-[#33101c] border border-[#4d1a2a] hover:border-[#d3b870] transition-colors">
                <span className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c]">{t.dg2_k}</span>
                <h3 className="font-display uppercase text-lg text-[#faf6ef] mt-2">{t.dg2_t}</h3>
                <p className="text-sm text-[#b4939c] mt-2">{t.dg2_d}</p>
              </div>
              <div className="p-6 bg-[#33101c] border border-[#4d1a2a] hover:border-[#d3b870] transition-colors">
                <span className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c]">{t.dg3_k}</span>
                <h3 className="font-display uppercase text-lg text-[#faf6ef] mt-2">{t.dg3_t}</h3>
                <p className="text-sm text-[#b4939c] mt-2">{t.dg3_d}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARLANO DI NOI — rassegna stampa */}
      <section id="parlano-di-noi" className="bg-[#200910] text-[#faf6ef] border-b border-[#4d1a2a]">
        <PressStrip label={t.ts_kicker} />
      </section>

      {/* REPORT */}
      <section className="bg-[#33101c] text-[#faf6ef] py-24 px-6 border-b border-[#4d1a2a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.rp_kicker}</span>
            <h2 className="font-display uppercase text-[clamp(32px,4vw,56px)] leading-[0.98] mt-2">
              {t.rp_title}
            </h2>
            <p className="text-sm text-[#b4939c] mt-3">{t.rp_sub}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.rp_cards.map((c) => (
              <div key={c.l} className="p-6 bg-[#200910] border border-[#4d1a2a]">
                <div className="w-8 h-8 bg-[#d3b870]/10 text-[#d3b870] font-mono-lbl flex items-center justify-center font-bold mb-4">{c.l}</div>
                <h4 className="font-display uppercase text-base text-[#faf6ef]">{c.t}</h4>
                <p className="text-xs text-[#b4939c] mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERCORSO */}
      <section id="percorso" className="bg-[#200910] text-[#faf6ef] py-24 px-6 border-b border-[#4d1a2a]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16">
            <div>
              <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.pc_kicker}</span>
              <h2 className="font-display uppercase text-[clamp(32px,4vw,56px)] leading-[0.98] mt-2">
                {t.pc_title}
              </h2>
            </div>
            <p className="text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c] mt-4 md:mt-0">
              {t.pc_side}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {t.pc_steps.map((s, i) => (
              <div key={s.n} className={`border-t-2 pt-6 ${i === 0 ? "border-[#d3b870]" : "border-[#4d1a2a]"}`}>
                <span className={`font-mono-lbl text-xs ${i === 0 ? "text-[#d3b870]" : "text-[#b4939c]"}`}>{s.n}</span>
                <h4 className="font-display uppercase text-lg mt-2 text-[#faf6ef]">{s.t}</h4>
                <p className="text-xs text-[#b4939c] mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACCHETTI */}
      <section id="pacchetti" className="bg-[#33101c] text-[#faf6ef] py-24 px-6 border-b border-[#4d1a2a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.pk_kicker}</span>
            <h2 className="font-display uppercase text-[clamp(32px,4vw,56px)] leading-[0.98] mt-2" data-testid="packages-title">
              {t.pk_title}
            </h2>
            <p className="text-sm text-[#b4939c] mt-3">{t.pk_sub}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.key}
                data-testid={`package-card-${pkg.key}`}
                className={`p-8 bg-[#200910] flex flex-col justify-between relative ${
                  pkg.featured ? "border-2 border-[#d3b870] shadow-2xl" : "border border-[#4d1a2a]"
                }`}
              >
                {pkg.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d3b870] text-[#200910] px-3 py-1 text-[10px] font-mono-lbl uppercase tracking-[0.2em] font-bold">
                    {t.pk_badge}
                  </div>
                )}
                <div>
                  <div className={`font-mono-lbl text-xs uppercase tracking-[0.14em] mb-2 ${pkg.featured ? "text-[#d3b870]" : "text-[#b4939c]"}`}>{pkg.name}</div>
                  <div className={`font-display text-4xl mb-4 ${pkg.featured ? "text-[#d3b870]" : "text-[#faf6ef]"}`}>
                    {pkg.price} <span className="text-xs font-mono-lbl font-normal text-[#b4939c]">{t.pk_iva}</span>
                  </div>
                  <p className="text-xs text-[#b4939c] mb-6">{pkg.desc}</p>
                  <ul className="text-xs space-y-3 border-t border-[#4d1a2a] pt-6 mb-8 font-mono-lbl">
                    {pkg.features.map((f) => (
                      <li key={f} className={`flex items-center gap-2 ${pkg.featured ? "text-[#d3b870]" : "text-[#faf6ef]"}`}>✓ {f}</li>
                    ))}
                    {pkg.excluded.map((f) => (
                      <li key={f} className="text-[#b4939c]">✕ {f}</li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  data-testid={pkg.testid}
                  disabled={loading !== null}
                  onClick={() => handleCheckout(pkg)}
                  className={
                    pkg.featured
                      ? "w-full py-4 bg-[#d3b870] hover:bg-[#ecd9ab] text-[#200910] font-display uppercase tracking-wider text-sm transition-colors font-bold disabled:opacity-60"
                      : "w-full py-3 border border-[#4d1a2a] hover:border-[#d3b870] text-xs font-mono-lbl uppercase tracking-[0.14em] text-[#faf6ef] hover:text-[#d3b870] transition-colors disabled:opacity-60"
                  }
                >
                  {loading === pkg.key ? t.pk_loading : `${t.pk_pay_pre} ${pkg.price} ${t.pk_pay_post}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#160508] text-[#b4939c] py-16 px-6 border-t border-[#4d1a2a]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-6 space-y-3">
            <img src="/logo-trust-master.png" alt="Trust Master" className="h-10 w-auto shrink-0" style={{ aspectRatio: "485/155" }} data-testid="footer-logo" />
            <p className="text-xs leading-relaxed max-w-md">
              {t.ft_company}
            </p>
            <p className="text-[11px] font-mono-lbl text-[#b4939c] leading-relaxed max-w-md" data-testid="footer-legal-data">
              {t.ft_legal}
            </p>
            <p className="text-[11px] text-[#8f6e77] leading-relaxed max-w-md">
              {t.ft_disclaimer}
            </p>
            <Link to="/privacy" data-testid="footer-privacy-link" className="inline-block text-[11px] text-[#d3b870] hover:text-[#ecd9ab] transition-colors font-mono-lbl uppercase tracking-[0.14em]">
              {t.ft_privacy}
            </Link>
          </div>
          <div className="md:col-span-3 text-xs space-y-2">
            <div className="font-mono-lbl uppercase tracking-[0.14em] text-[#d3b870]">{t.ft_lang_t}</div>
            {t.ft_langs.map((l) => <p key={l}>{l}</p>)}
          </div>
          <div className="md:col-span-3 text-xs space-y-2">
            <div className="font-mono-lbl uppercase tracking-[0.14em] text-[#d3b870]">{t.ft_pay_t}</div>
            <p>{t.ft_pay1}</p>
            <p>{t.ft_pay2}</p>
            <p>{t.ft_rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
